<template>
  <div>
    <job-menu :job="job"></job-menu>

    <div class="my-6 px-4">
      <div class="max-w-7xl mx-auto bg-white shadow-md p-6">
        <div class="flex items-center justify-between gap-4">
          <h1 class="text-2xl font-bold">{{ __("Report card") }}</h1>
          <div class="flex items-center gap-4 text-xs text-gray-600">
            <div><span class="text-gray-400">{{ __("Job") }}:</span> <span class="font-semibold text-gray-800">#{{ jobNumber }}</span></div>
            <a
              v-if="readonly"
              :href="route('reportCards.showPdf', { job: job.id, pdf: latestPdf.id })"
              class="flex items-center text-xl px-10 h-10 rounded-lg bg-coral-400 text-white border-2 border-coral-400 hover:bg-coral-500"
              target="_blank"
              rel="noopener noreferrer"
            >{{ __("Open PDF") }}</a>
            <template v-else>
              <a
                :href="route('reportCards.preview', { job: job.id })"
                class="flex items-center text-xl px-10 h-10 rounded-lg text-gray-500 border-2 border-gray-300 hover:bg-gray-100"
                target="_blank"
                rel="noopener noreferrer"
              >{{ __("PDF preview") }}</a>
              <button
                type="button"
                class="flex items-center text-xl px-10 h-10 rounded-lg bg-coral-400 text-white border-2 border-coral-400 hover:bg-coral-500"
                @click="onGenerateClick"
              >{{ __("Generate PDF") }}</button>
            </template>
          </div>
        </div>

        <div v-if="showIncompleteWarning" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div class="bg-white rounded-lg shadow-2xl max-w-lg w-full p-8">
            <div class="flex flex-col items-center text-center">
              <div class="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mb-5">
                <svg class="w-8 h-8 text-yellow-600 fill-current" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.13 2.5a1 1 0 0 1 1.74 0l8.65 14.99a1 1 0 0 1-.87 1.51H1.35a1 1 0 0 1-.87-1.51L9.13 2.5zM9 8v4h2V8H9zm0 5v2h2v-2H9z"/>
                </svg>
              </div>
              <h2 class="text-xl font-bold mb-3">{{ __("Report card not complete") }}</h2>
              <p class="text-sm text-gray-600 leading-relaxed">
                {{ __("To finalize this job, you must complete the report card. At least one item must have an investigated report, an operation and a technician selected, and the road test user and supervisor must be set.") }}
              </p>
            </div>
            <div class="mt-8 flex justify-center">
              <button type="button" class="px-8 py-2 rounded bg-coral-400 text-white border border-coral-400 hover:bg-coral-500" @click="showIncompleteWarning = false">
                {{ __("OK") }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="showGenerateConfirm" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div class="bg-white rounded-lg shadow-2xl max-w-lg w-full p-8">
            <div class="flex flex-col items-center text-center">
              <div class="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-5">
                <svg class="w-8 h-8 text-blue-600 fill-current" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4a2 2 0 0 1 2-2h7.59a2 2 0 0 1 1.41.59L18.41 6A2 2 0 0 1 19 7.41V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4zm9 0v4h4l-4-4zM7 9h6v2H7V9zm0 4h6v2H7v-2z"/>
                </svg>
              </div>
              <h2 class="text-xl font-bold mb-3">{{ __("Generate PDF") }}</h2>
              <p class="text-sm text-gray-600 leading-relaxed">
                {{ __("This will save a permanent copy of the report card. The current date and time will be stamped next to the road test user and the supervisor as their sign-off date. Continue?") }}
              </p>
            </div>
            <div class="mt-8 flex justify-center gap-3">
              <button type="button" class="px-6 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100" @click="showGenerateConfirm = false">
                {{ __("Cancel") }}
              </button>
              <loading-button :loading="generatingPdf" type="button" class="justify-center px-6 py-2 rounded bg-coral-400 text-white hover:bg-coral-500" @click="confirmGeneratePdf">
                {{ __("Generate PDF") }}
              </loading-button>
            </div>
          </div>
        </div>

        <div class="mt-6">
          <div>
            <h2 class="text-base font-bold">{{ __("Items") }} <span class="text-gray-400 font-normal">({{ form.items.length }})</span></h2>
          </div>

          <div v-if="form.items.length === 0 && !readonly" class="mt-3 rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            <div>{{ __("No items yet. Click 'Add item' to start.") }}</div>
            <div class="mt-3 flex justify-center">
              <button type="button" class="btn-primary text-xs py-1 px-3" @click="addItem">
                {{ __("Add item") }}
              </button>
            </div>
          </div>

          <div v-for="(item, index) in form.items" :key="item.id || ('new-' + index)" class="mt-4 rounded-lg border border-gray-300 bg-white shadow">
            <div class="flex items-center justify-between bg-gray-100 border-b border-gray-300 rounded-t-lg px-3 py-2">
              <div class="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span class="inline-flex w-6 h-6 items-center justify-center rounded-full bg-white border border-gray-400 text-xs font-bold">{{ index + 1 }}</span>
                {{ __("Item") }}
              </div>
              <button v-if="!readonly" type="button" class="text-xs text-red-500 hover:text-red-700" @click="removeItem(index)">
                {{ __("Remove") }}
              </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
              <div class="flex flex-col h-36">
                <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                  {{ __("Investigated Report") }} <span class="text-red-500">*</span>
                </label>
                <div class="flex-1 flex flex-col border rounded focus-within:border-coral-400 overflow-hidden" :class="[readonly ? 'border-gray-300 bg-gray-100' : (isReportEmpty(item) ? 'border-red-400' : 'border-gray-300')]">
                  <div v-if="!readonly" class="flex items-center gap-1 bg-gray-100 border-b border-gray-200 px-2 py-1">
                    <button
                      type="button"
                      :title="__('Red')"
                      class="w-6 h-6 flex items-center justify-center text-sm font-bold rounded text-red-600"
                      :class="activeEditorColor(index, 'investigated_report') === '#dc2626' ? 'bg-red-100 ring-1 ring-red-300' : 'hover:bg-red-50'"
                      @mousedown.prevent
                      @click="applyColor($event, item, 'investigated_report', '#dc2626')"
                    >A</button>
                    <button
                      type="button"
                      :title="__('Green')"
                      class="w-6 h-6 flex items-center justify-center text-sm font-bold rounded text-green-600"
                      :class="activeEditorColor(index, 'investigated_report') === '#16a34a' ? 'bg-green-100 ring-1 ring-green-300' : 'hover:bg-green-50'"
                      @mousedown.prevent
                      @click="applyColor($event, item, 'investigated_report', '#16a34a')"
                    >A</button>
                    <button
                      type="button"
                      :title="__('Default')"
                      class="w-6 h-6 flex items-center justify-center text-sm font-bold rounded text-gray-700"
                      :class="activeEditorColor(index, 'investigated_report') === '#111827' ? 'bg-gray-200 ring-1 ring-gray-400' : 'hover:bg-gray-200'"
                      @mousedown.prevent
                      @click="applyColor($event, item, 'investigated_report', '#111827')"
                    >A</button>
                  </div>
                  <div
                    :contenteditable="!readonly"
                    :data-editor-index="index"
                    data-editor-field="investigated_report"
                    class="flex-1 px-2 py-1 focus:outline-none text-sm leading-snug overflow-auto"
                    :class="readonly ? 'bg-gray-100 text-gray-500' : ''"
                    @input="onEditorInput(item, 'investigated_report', $event)"
                    @keyup="updateEditorActiveColor(index, 'investigated_report')"
                    @mouseup="updateEditorActiveColor(index, 'investigated_report')"
                    @focus="updateEditorActiveColor(index, 'investigated_report')"
                  ></div>
                </div>
                <div v-if="errorFor(index, 'investigated_report')" class="text-red-500 text-xs mt-1">
                  {{ errorFor(index, 'investigated_report') }}
                </div>
              </div>

              <div class="flex flex-col h-36">
                <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                  {{ __("Technicians Write Up") }}
                </label>
                <div class="flex-1 flex flex-col border rounded focus-within:border-coral-400 overflow-hidden" :class="readonly ? 'border-gray-300 bg-gray-100' : 'border-gray-300'">
                  <div v-if="!readonly" class="flex items-center gap-1 bg-gray-100 border-b border-gray-200 px-2 py-1">
                    <button
                      type="button"
                      :title="__('Red')"
                      class="w-6 h-6 flex items-center justify-center text-sm font-bold rounded text-red-600"
                      :class="activeEditorColor(index, 'technicians_write_up') === '#dc2626' ? 'bg-red-100 ring-1 ring-red-300' : 'hover:bg-red-50'"
                      @mousedown.prevent
                      @click="applyColor($event, item, 'technicians_write_up', '#dc2626')"
                    >A</button>
                    <button
                      type="button"
                      :title="__('Green')"
                      class="w-6 h-6 flex items-center justify-center text-sm font-bold rounded text-green-600"
                      :class="activeEditorColor(index, 'technicians_write_up') === '#16a34a' ? 'bg-green-100 ring-1 ring-green-300' : 'hover:bg-green-50'"
                      @mousedown.prevent
                      @click="applyColor($event, item, 'technicians_write_up', '#16a34a')"
                    >A</button>
                    <button
                      type="button"
                      :title="__('Default')"
                      class="w-6 h-6 flex items-center justify-center text-sm font-bold rounded text-gray-700"
                      :class="activeEditorColor(index, 'technicians_write_up') === '#111827' ? 'bg-gray-200 ring-1 ring-gray-400' : 'hover:bg-gray-200'"
                      @mousedown.prevent
                      @click="applyColor($event, item, 'technicians_write_up', '#111827')"
                    >A</button>
                  </div>
                  <div
                    :contenteditable="!readonly"
                    :data-editor-index="index"
                    data-editor-field="technicians_write_up"
                    class="flex-1 px-2 py-1 focus:outline-none text-sm leading-snug overflow-auto"
                    :class="readonly ? 'bg-gray-100 text-gray-500' : ''"
                    @input="onEditorInput(item, 'technicians_write_up', $event)"
                    @keyup="updateEditorActiveColor(index, 'technicians_write_up')"
                    @mouseup="updateEditorActiveColor(index, 'technicians_write_up')"
                    @focus="updateEditorActiveColor(index, 'technicians_write_up')"
                  ></div>
                </div>
                <div v-if="errorFor(index, 'technicians_write_up')" class="text-red-500 text-xs mt-1">
                  {{ errorFor(index, 'technicians_write_up') }}
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 gap-3 px-3 pb-3">
              <div :class="{ 'field-missing': !readonly && !item.operation_catalog_id }">
                <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                  {{ __("Operation") }} <span v-if="!readonly" class="text-red-500">*</span>
                </label>
                <multiselect
                  :value="operationValueFor(item)"
                  :options="operationOptionsFor(index, item)"
                  track-by="id"
                  label="name"
                  :placeholder="__('Search operation…')"
                  :show-labels="false"
                  :allow-empty="true"
                  :disabled="readonly"
                  :loading="operationLoading[index]"
                  :internal-search="false"
                  :options-limit="50"
                  @search-change="(q) => searchOperations(index, q)"
                  @open="() => searchOperations(index, '')"
                  @input="(v) => setOperation(item, v)"
                ></multiselect>
                <div v-if="errorFor(index, 'operation_catalog_id')" class="text-red-500 text-xs mt-1">
                  {{ errorFor(index, 'operation_catalog_id') }}
                </div>
              </div>

              <div :class="{ 'field-missing': !readonly && !item.technician_id }">
                <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                  {{ __("Technician") }} <span v-if="!readonly" class="text-red-500">*</span>
                </label>
                <multiselect
                  :value="technicianValueFor(item)"
                  :options="technicians"
                  track-by="id"
                  label="name"
                  :custom-label="userLabel"
                  :placeholder="__('Search technician…')"
                  :show-labels="false"
                  :allow-empty="true"
                  :disabled="readonly"
                  @input="(v) => setTechnician(item, v)"
                >
                  <template slot="noOptions">{{ __("List is empty.") }}</template>
                  <template slot="noResult">{{ __("No elements found. Consider changing the search query.") }}</template>
                </multiselect>
                <div v-if="errorFor(index, 'technician_id')" class="text-red-500 text-xs mt-1">
                  {{ errorFor(index, 'technician_id') }}
                </div>
              </div>

              <div>
                <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                  {{ __("Started") }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    type="date"
                    v-model="item.operation_started_date"
                    :max="item.operation_ended_date || null"
                    :disabled="readonly"
                    class="block flex-1 min-w-0 h-10 border border-gray-300 rounded px-2 text-sm focus:outline-none focus:border-coral-400"
                    :class="readonly ? 'bg-gray-100 text-gray-500' : ''"
                    @input="syncOperationDateTime(item, 'operation_started')"
                  />
                  <select
                    v-model="item.operation_started_hour"
                    :disabled="readonly"
                    class="block flex-none w-16 h-10 border border-gray-300 rounded bg-white px-2 text-sm focus:outline-none focus:border-coral-400"
                    :class="readonly ? 'bg-gray-100 text-gray-500' : 'text-gray-700'"
                    @change="syncOperationDateTime(item, 'operation_started')"
                  >
                    <option value="">--</option>
                    <option v-for="hour in hourOptions" :key="'started-hour-' + hour" :value="hour">{{ hour }}</option>
                  </select>
                  <select
                    v-model="item.operation_started_minute"
                    :disabled="readonly"
                    class="block flex-none w-16 h-10 border border-gray-300 rounded bg-white px-2 text-sm focus:outline-none focus:border-coral-400"
                    :class="readonly ? 'bg-gray-100 text-gray-500' : 'text-gray-700'"
                    @change="syncOperationDateTime(item, 'operation_started')"
                  >
                    <option value="">--</option>
                    <option v-for="minute in minuteOptions" :key="'started-minute-' + minute" :value="minute">{{ minute }}</option>
                  </select>
                </div>
                <div v-if="errorFor(index, 'operation_started_at')" class="text-red-500 text-xs mt-1">
                  {{ errorFor(index, 'operation_started_at') }}
                </div>
              </div>

              <div>
                <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                  {{ __("Ended") }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    type="date"
                    v-model="item.operation_ended_date"
                    :min="item.operation_started_date || null"
                    :disabled="readonly"
                    class="block flex-1 min-w-0 h-10 border border-gray-300 rounded px-2 text-sm focus:outline-none focus:border-coral-400"
                    :class="readonly ? 'bg-gray-100 text-gray-500' : ''"
                    @input="syncOperationDateTime(item, 'operation_ended')"
                  />
                  <select
                    v-model="item.operation_ended_hour"
                    :disabled="readonly"
                    class="block flex-none w-16 h-10 border border-gray-300 rounded bg-white px-2 text-sm focus:outline-none focus:border-coral-400"
                    :class="readonly ? 'bg-gray-100 text-gray-500' : 'text-gray-700'"
                    @change="syncOperationDateTime(item, 'operation_ended')"
                  >
                    <option value="">--</option>
                    <option v-for="hour in hourOptions" :key="'ended-hour-' + hour" :value="hour">{{ hour }}</option>
                  </select>
                  <select
                    v-model="item.operation_ended_minute"
                    :disabled="readonly"
                    class="block flex-none w-16 h-10 border border-gray-300 rounded bg-white px-2 text-sm focus:outline-none focus:border-coral-400"
                    :class="readonly ? 'bg-gray-100 text-gray-500' : 'text-gray-700'"
                    @change="syncOperationDateTime(item, 'operation_ended')"
                  >
                    <option value="">--</option>
                    <option v-for="minute in minuteOptions" :key="'ended-minute-' + minute" :value="minute">{{ minute }}</option>
                  </select>
                </div>
                <div v-if="errorFor(index, 'operation_ended_at')" class="text-red-500 text-xs mt-1">
                  {{ errorFor(index, 'operation_ended_at') }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="form.items.length > 0 && !readonly" class="mt-4 flex justify-center">
            <button type="button" class="text-sm text-coral-500 hover:text-coral-700 font-semibold" @click="addItem">
              + {{ __("Add item") }}
            </button>
          </div>

          <div class="mt-6 space-y-3">
            <div class="rounded-lg border border-gray-300 bg-white shadow p-3 flex flex-col h-32">
              <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                {{ __("Displaced Parts") }}
              </label>
              <textarea
                v-model="form.displaced_parts"
                :disabled="readonly"
                class="flex-1 block w-full border border-gray-300 rounded px-2 py-1 text-sm leading-snug resize-none focus:outline-none focus:border-coral-400"
                :class="readonly ? 'bg-gray-100 text-gray-500' : ''"
              ></textarea>
            </div>
            <div class="rounded-lg border border-gray-300 bg-white shadow p-3">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <label class="block text-xs font-medium uppercase tracking-wider text-gray-400">
                    {{ __("Additional Repairs / Technicians Comments") }}
                  </label>
                </div>
                <button
                  v-if="!readonly"
                  type="button"
                  class="flex-none text-xs text-coral-500 hover:text-coral-700 font-semibold"
                  @click="addTechnicianComment"
                >
                  + {{ __("Add comment") }}
                </button>
              </div>
              <div v-if="form.technician_comments.length === 0" class="mt-3 flex items-center justify-center border border-dashed border-gray-300 rounded py-6 text-xs text-gray-400">
                {{ __("No technician comments") }}
              </div>
              <div v-else class="mt-3 divide-y divide-gray-200">
                <div
                  v-for="(comment, commentIndex) in form.technician_comments"
                  :key="comment.id || ('comment-' + commentIndex)"
                  class="py-3 first:pt-0 last:pb-0"
                >
                  <div class="flex flex-col lg:flex-row lg:items-start gap-3">
                    <div class="lg:w-72 flex-none">
                      <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                        {{ __("Technician") }}
                      </label>
                      <multiselect
                        :value="technicianCommentUserValue(comment)"
                        :options="technicians"
                        track-by="id"
                        label="name"
                        :custom-label="userLabel"
                        :placeholder="__('Search technician…')"
                        :show-labels="false"
                        :allow-empty="true"
                        :disabled="readonly"
                        @input="(v) => setTechnicianCommentUser(comment, v)"
                      >
                        <template slot="noOptions">{{ __("List is empty.") }}</template>
                        <template slot="noResult">{{ __("No elements found. Consider changing the search query.") }}</template>
                      </multiselect>
                      <div v-if="errorForTechnicianComment(commentIndex, 'user_id')" class="text-red-500 text-xs mt-1">
                        {{ errorForTechnicianComment(commentIndex, 'user_id') }}
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                        {{ __("Comment") }}
                      </label>
                      <div class="flex items-start gap-2">
                        <textarea
                          v-model="comment.body"
                          :disabled="readonly"
                          rows="2"
                          :placeholder="__('Write the technician comment…')"
                          class="block w-full min-h-10 border border-gray-300 rounded bg-white px-3 py-2 text-sm leading-snug resize-y focus:outline-none focus:border-coral-400"
                          :class="readonly ? 'bg-gray-100 text-gray-500' : ''"
                        ></textarea>
                        <button
                          v-if="!readonly"
                          type="button"
                          class="flex-none mt-1 text-gray-400 hover:text-red-500 text-lg leading-none px-1"
                          @click="removeTechnicianComment(commentIndex)"
                          aria-label="Remove technician comment"
                        >
                          &times;
                        </button>
                      </div>
                      <div v-if="errorForTechnicianComment(commentIndex, 'body')" class="text-red-500 text-xs mt-1">
                        {{ errorForTechnicianComment(commentIndex, 'body') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3 rounded-lg border border-gray-300 bg-white shadow p-3">
            <h3 class="text-sm font-bold mb-2">{{ __("Final Approval") }}</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div :class="{ 'field-missing': !readonly && !form.road_test_user_id }">
                <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                  {{ __("Road Test Carried out by") }} <span v-if="!readonly" class="text-red-500">*</span>
                </label>
                <multiselect
                  :value="roadTestUserValue"
                  :options="technicians"
                  track-by="id"
                  label="name"
                  :custom-label="userLabel"
                  :placeholder="__('Search user…')"
                  :show-labels="false"
                  :allow-empty="true"
                  :disabled="readonly"
                  @input="setRoadTestUser"
                >
                  <template slot="noOptions">{{ __("List is empty.") }}</template>
                  <template slot="noResult">{{ __("No elements found. Consider changing the search query.") }}</template>
                </multiselect>
                <div v-if="error('road_test_user_id')" class="text-red-500 text-xs mt-1">
                  {{ error('road_test_user_id') }}
                </div>
              </div>
              <div :class="{ 'field-missing': !readonly && !form.supervisor_user_id }">
                <label class="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
                  {{ __("Supervisor") }} <span v-if="!readonly" class="text-red-500">*</span>
                </label>
                <multiselect
                  :value="supervisorValue"
                  :options="supervisors"
                  track-by="id"
                  label="name"
                  :custom-label="userLabel"
                  :placeholder="__('Search user…')"
                  :show-labels="false"
                  :allow-empty="true"
                  :disabled="readonly"
                  @input="setSupervisor"
                >
                  <template slot="noOptions">{{ __("List is empty.") }}</template>
                  <template slot="noResult">{{ __("No elements found. Consider changing the search query.") }}</template>
                </multiselect>
                <div v-if="error('supervisor_user_id')" class="text-red-500 text-xs mt-1">
                  {{ error('supervisor_user_id') }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="!readonly" class="mt-4 flex justify-end">
            <loading-button :loading="form.processing" class="btn-primary" @click="save">
              {{ __("Save") }}
            </loading-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Layout from '@/Shared/Layout';
  import LoadingButton from '@/Shared/LoadingButton';
  import Multiselect from 'vue-multiselect';
  import JobMenu from './JobMenu';

  function toLocalDateTimeParts(value) {
    if (!value) {
      return {
        value: '',
        date: '',
        hour: '',
        minute: '',
      };
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return {
        value: '',
        date: '',
        hour: '',
        minute: '',
      };
    }
    const pad = (n) => String(n).padStart(2, '0');
    const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const hour = pad(d.getHours());
    const minute = pad(d.getMinutes());

    return {
      value: `${date}T${hour}:${minute}`,
      date,
      hour,
      minute,
    };
  }

  export default {
    layout: Layout,
    metaInfo() {
      return {
        title: this.__("Report card"),
      }
    },
    props: {
      job: Object,
      reportCard: Object,
      technicians: Array,
      supervisors: Array,
      latestPdf: Object,
      jobCreatedAtLocal: String,
      jobTechnicianId: Number,
    },
    components: {
      JobMenu,
      LoadingButton,
      Multiselect,
    },
    data() {
      const items = this.reportCard.items || [];
      return {
        hourOptions: Array.from({ length: 10 }, (_, i) => String(i + 8).padStart(2, '0')),
        minuteOptions: Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')),
        form: this.$inertia.form({
          road_test_user_id: this.reportCard.road_test_user_id || null,
          supervisor_user_id: this.reportCard.supervisor_user_id || null,
          displaced_parts: this.reportCard.displaced_parts || '',
          technicians_comments: this.reportCard.technicians_comments || '',
          technician_comments: (this.reportCard.technician_comments || []).map(comment => ({
            id: comment.id,
            user_id: comment.user_id || null,
            body: comment.body || '',
          })),
          items: items.map(item => {
            const startedAt = toLocalDateTimeParts(item.operation_started_at);
            const endedAt = toLocalDateTimeParts(item.operation_ended_at);

            return {
              id: item.id,
              investigated_report: item.investigated_report || '',
              technicians_write_up: item.technicians_write_up || '',
              operation_catalog_id: item.operation_catalog_id || null,
              technician_id: item.technician_id || null,
              operation_started_at: startedAt.value,
              operation_started_date: startedAt.date,
              operation_started_hour: startedAt.hour,
              operation_started_minute: startedAt.minute,
              operation_ended_at: endedAt.value,
              operation_ended_date: endedAt.date,
              operation_ended_hour: endedAt.hour,
              operation_ended_minute: endedAt.minute,
            };
          }),
        }),
        operationSelected: items.map(item => item.operation_catalog ? { id: item.operation_catalog.id, name: item.operation_catalog.name } : null),
        operationResults: items.map(() => []),
        operationLoading: items.map(() => false),
        operationSearchToken: items.map(() => 0),
        editorActiveColor: {},
        showGenerateConfirm: false,
        showIncompleteWarning: false,
        generatingPdf: false,
      };
    },
    computed: {
      roadTestUserValue() {
        return this.technicians.find(t => t.id === this.form.road_test_user_id) || null;
      },
      supervisorValue() {
        return this.supervisors.find(t => t.id === this.form.supervisor_user_id) || null;
      },
      isReportCardComplete() {
        if (!this.form.road_test_user_id || !this.form.supervisor_user_id) return false;
        return (this.form.items || []).some(item => {
          if (!item.operation_catalog_id || !item.technician_id) return false;
          const stripped = (item.investigated_report || '').replace(/<[^>]*>/g, '').trim();
          return stripped !== '';
        });
      },
      readonly() {
        return !!this.latestPdf;
      },
      jobNumber() {
        return this.job.external_id || '-';
      },
    },
    mounted() {
      this.seedEditors();
    },
    methods: {
      seedEditors() {
        this.$nextTick(() => {
          this.$el.querySelectorAll('[data-editor-index][data-editor-field]').forEach(el => {
            const i = parseInt(el.dataset.editorIndex, 10);
            const field = el.dataset.editorField;
            const html = this.form.items[i] ? (this.form.items[i][field] || '') : '';
            if (el.innerHTML !== html) el.innerHTML = html;
          });
        });
      },
      addItem() {
        const startedAt = this.defaultOperationDateTime();

        this.form.items.push({
          id: null,
          investigated_report: '',
          technicians_write_up: '',
          operation_catalog_id: null,
          technician_id: this.jobTechnicianId || null,
          operation_started_at: startedAt.value,
          operation_started_date: startedAt.date,
          operation_started_hour: startedAt.hour,
          operation_started_minute: startedAt.minute,
          operation_ended_at: '',
          operation_ended_date: '',
          operation_ended_hour: '',
          operation_ended_minute: '',
        });
        this.operationSelected.push(null);
        this.operationResults.push([]);
        this.operationLoading.push(false);
        this.operationSearchToken.push(0);
        this.seedEditors();
      },
      removeItem(index) {
        this.form.items.splice(index, 1);
        this.operationSelected.splice(index, 1);
        this.operationResults.splice(index, 1);
        this.operationLoading.splice(index, 1);
        this.operationSearchToken.splice(index, 1);
        this.seedEditors();
      },
      operationValueFor(item) {
        const id = item.operation_catalog_id;
        if (!id) return null;
        const index = this.form.items.indexOf(item);
        if (index === -1) return null;
        const selected = this.operationSelected[index];
        if (selected && selected.id === id) return selected;
        const found = this.operationResults[index].find(o => o.id === id);
        return found || null;
      },
      operationOptionsFor(index, item) {
        const list = this.operationResults[index] || [];
        const selected = this.operationSelected[index];
        if (selected && !list.some(o => o.id === selected.id)) {
          return [selected, ...list];
        }
        return list;
      },
      setOperation(item, value) {
        const index = this.form.items.indexOf(item);
        item.operation_catalog_id = value ? value.id : null;
        if (index !== -1) {
          this.$set(this.operationSelected, index, value || null);
        }
      },
      searchOperations(index, query) {
        const token = this.operationSearchToken[index] + 1;
        this.$set(this.operationSearchToken, index, token);
        this.$set(this.operationLoading, index, true);
        window.axios.get(this.route('operationCatalogs.search'), { params: { q: query || '' } })
          .then(response => {
            if (this.operationSearchToken[index] !== token) return;
            this.$set(this.operationResults, index, response.data || []);
          })
          .catch(() => {
            if (this.operationSearchToken[index] !== token) return;
            this.$set(this.operationResults, index, []);
          })
          .then(() => {
            if (this.operationSearchToken[index] === token) {
              this.$set(this.operationLoading, index, false);
            }
          });
      },
      applyColor(event, item, field, color) {
        const editor = event.currentTarget.closest('.border').querySelector('[contenteditable]');
        if (!editor) return;
        editor.focus();
        document.execCommand('styleWithCSS', false, true);
        document.execCommand('foreColor', false, color);
        item[field] = editor.innerHTML;
        const index = this.form.items.indexOf(item);
        if (index !== -1) {
          this.$set(this.editorActiveColor, this.editorKey(index, field), color);
        }
      },
      onEditorInput(item, field, event) {
        item[field] = event.target.innerHTML;
      },
      isReportEmpty(item) {
        return !((item.investigated_report || '').replace(/<[^>]*>/g, '').trim());
      },
      updateEditorActiveColor(index, field) {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) {
          this.$set(this.editorActiveColor, this.editorKey(index, field), null);
          return;
        }
        const editor = this.$el.querySelector(`[data-editor-index="${index}"][data-editor-field="${field}"]`);
        if (!editor) return;
        const range = sel.getRangeAt(0);
        let node = range.startContainer;
        if (!editor.contains(node)) {
          this.$set(this.editorActiveColor, this.editorKey(index, field), null);
          return;
        }
        if (node.nodeType === 3) node = node.parentElement;
        let color = null;
        while (node && node !== editor) {
          if (node.style && node.style.color) {
            color = node.style.color;
            break;
          }
          node = node.parentElement;
        }
        const hex = this.cssColorToHex(color);
        this.$set(this.editorActiveColor, this.editorKey(index, field), hex);
      },
      editorKey(index, field) {
        return `${index}:${field}`;
      },
      activeEditorColor(index, field) {
        return this.editorActiveColor[this.editorKey(index, field)] || null;
      },
      userLabel(option) {
        if (!option) return '';
        return option.reference_id ? `${option.name} (${option.reference_id})` : option.name;
      },
      cssColorToHex(c) {
        if (!c) return '#111827';
        const v = c.toLowerCase().trim();
        if (/^#[0-9a-f]{6}$/.test(v)) return v;
        const m = v.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
        if (m) {
          const toHex = n => Number(n).toString(16).padStart(2, '0');
          return '#' + toHex(m[1]) + toHex(m[2]) + toHex(m[3]);
        }
        return null;
      },
      technicianValueFor(item) {
        return this.technicians.find(t => t.id === item.technician_id) || null;
      },
      setTechnician(item, value) {
        item.technician_id = value ? value.id : null;
      },
      setRoadTestUser(value) {
        this.form.road_test_user_id = value ? value.id : null;
      },
      setSupervisor(value) {
        this.form.supervisor_user_id = value ? value.id : null;
      },
      addTechnicianComment() {
        this.form.technician_comments.push({
          id: null,
          user_id: this.jobTechnicianId || null,
          body: '',
        });
      },
      removeTechnicianComment(index) {
        this.form.technician_comments.splice(index, 1);
      },
      technicianCommentUserValue(comment) {
        return this.technicians.find(t => t.id === comment.user_id) || null;
      },
      setTechnicianCommentUser(comment, value) {
        comment.user_id = value ? value.id : null;
      },
      defaultOperationDateTime() {
        const parts = toLocalDateTimeParts(this.jobCreatedAtLocal || '');
        if (parts.date && !this.hourOptions.includes(parts.hour)) {
          parts.hour = '08';
          parts.value = `${parts.date}T${parts.hour}:${parts.minute || '00'}`;
        }

        return parts;
      },
      syncOperationDateTime(item, prefix) {
        const date = item[`${prefix}_date`];
        const hour = item[`${prefix}_hour`];
        const minute = item[`${prefix}_minute`];
        item[`${prefix}_at`] = date && hour && minute ? `${date}T${hour}:${minute}` : '';
      },
      save() {
        this.form.items.forEach(item => {
          this.syncOperationDateTime(item, 'operation_started');
          this.syncOperationDateTime(item, 'operation_ended');
        });
        this.form.patch(this.route('reportCards.update', { job: this.job.id }), {
          preserveScroll: true,
        });
      },
      onGenerateClick() {
        if (!this.isReportCardComplete) {
          this.showIncompleteWarning = true;
          return;
        }
        this.showGenerateConfirm = true;
      },
      confirmGeneratePdf() {
        this.generatingPdf = true;
        this.$inertia.post(this.route('reportCards.generatePdf', { job: this.job.id }), {}, {
          preserveScroll: true,
          onFinish: () => {
            this.generatingPdf = false;
            this.showGenerateConfirm = false;
          },
        });
      },
      errorFor(index, field) {
        const bag = this.$page.props.errors && this.$page.props.errors.default;
        if (!bag) return null;
        const messages = bag[`items.${index}.${field}`];
        return Array.isArray(messages) ? messages[0] : messages || null;
      },
      errorForTechnicianComment(index, field) {
        const bag = this.$page.props.errors && this.$page.props.errors.default;
        if (!bag) return null;
        const messages = bag[`technician_comments.${index}.${field}`];
        return Array.isArray(messages) ? messages[0] : messages || null;
      },
    },
  }
</script>
